// PostDetailPage.tsx — /post/:handle?/:id. Public (Growth slice, 1.21.60): reads the real
// API for both guests and signed-in viewers — a shared post link must resolve without an
// account.
import { useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, ConsolePanel } from '@/ui';
import {
  usePost,
  useUser,
  useSession,
  usePostHydration,
  usePageTitle,
  usePostThread,
  useIsOwn,
} from '@/hooks';
import { postPath } from '@/core/lib/postUrl';
import { startThreadPart } from '@/core/stores/threadDraftStore';
import { useComposeSheetStore } from '@/core/stores/composeSheetStore';
import { GuestBanner } from '@/components/GuestBanner';
import { useRightRail } from '@/app/shell/RightRail/context';
import { RailGroup, RailPanel } from '@/app/shell/RightRail';
import { HeroPostCard, ThreadPageScope } from '@/components/PostCard';
import { DiscussionGuideEntry } from '@/features/networks/ContextTools';
import { TrendingPanel } from '@/components/panels/TrendingPanel';
import { WhoToFollowPanel } from '@/components/panels/WhoToFollowPanel';
import { PageHeader } from './PageHeader';
import { CommentsSection } from './CommentsSection';
import { ThreadList } from './ThreadList';
import { AboutAuthorPanel } from './panels/AboutAuthorPanel';
import { DiscussionToolsProvider } from '@/features/discussion-tools/DiscussionToolsProvider';
import { DiscussionToolbar } from '@/features/discussion-tools/DiscussionTools';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const NotFound = styled(ConsolePanel)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 24px;
  text-align: center;
`;
const NotFoundText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const HomeLink = styled(Link)`
  color: ${({ theme }) => theme.color.brandFrom};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;
const ThreadActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

/**
 * Percent-decodes a pathname for COMPARISON only (1.24.50). `decodeURIComponent`
 * throws on a malformed sequence — a hand-typed `/post/%zz/x` is enough — and a
 * throw inside the relabel effect would take the whole page down over an address
 * we were only ever going to compare. An undecodable path is returned as-is:
 * worst case the two spellings differ and the address is relabelled once, which
 * is exactly what should happen to a malformed one.
 */
function decodePath(path: string): string {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

export function PostDetailPage() {
  // `handle` is read by the ROUTE only, never here: nothing resolves on it. A
  // stale, renamed or plain wrong handle still finds the post by its ident and
  // is corrected by the relabel below — that is what makes a handle rename
  // harmless instead of a mass link breakage.
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { post } = usePost(id ?? '');
  // `post?.id` is the RESOLVED legacy id — see usePostHydration: it is what
  // stops the canonical rewrite below from costing a second identical fetch.
  usePostHydration(id ?? '', post?.id);
  // 1.24.00 D1.2, extended by 1.24.50 — one post, one address. Links arrive in
  // every shape there is: a bare legacy id from a notification or a bookmark, a
  // bare share code from a link somebody copied before this release, a named URL
  // carrying a handle its author has since changed. Relabel to the canonical
  // address once the post is in hand. `replace` adds no history entry, so Back
  // still goes where the reader came from.
  //
  // The guard asks "is the WHOLE ADDRESS canonical?", not "is the ident
  // canonical?". Asking only about the ident returns early exactly when the
  // ident is already the share code — i.e. on every in-app link — which would
  // leave the named form inert on the commonest arrival of all.
  //
  // Compared DECODED to DECODED. A pathname can reach us holding a literal `@`
  // (a browser leaves one alone in a path — it is a legal pchar, so a pasted or
  // hand-typed `/post/@odd/…` arrives verbatim), while `encodeURIComponent`
  // inside postPath writes `%40`. Compared raw, those two spellings of ONE
  // address never look equal, so a reader who is already exactly where they
  // belong gets their address rewritten out from under them. Whether that then
  // settles depends entirely on the router handing the encoded form back — it
  // does today, so it costs one spurious history entry rather than spinning,
  // but the guard should not be resting on that.
  //
  // `search` and `hash` ride along. `navigate(postPath(post))` alone drops them,
  // and now that EVERY bare arrival relabels, that would silently strip the
  // utm/campaign query off every link anyone shares and drop the reader out of
  // whatever anchor they followed.
  //
  // Cannot loop: after the rewrite the address IS `postPath(post)`, so the
  // condition is false on the next render.
  useEffect(() => {
    if (!post) return;
    const canonical = postPath(post);
    if (decodePath(location.pathname) === decodePath(canonical)) return;
    navigate(`${canonical}${location.search}${location.hash}`, { replace: true });
  }, [post, location.pathname, location.search, location.hash, navigate]);
  const { isAuthenticated, currentUser } = useSession();
  const author = useUser(post?.authorId);
  const composerRef = useRef<HTMLDivElement>(null);
  // 1.24.00 C2 fix round: the post payload already answers "is this post in a
  // thread?" — the server serializes `thread` for every member of one, the root
  // included (position 1). So the /thread/ request is only worth making when
  // that field is set. It used to fire on EVERY post-detail view, and the
  // overwhelmingly common case is a stand-alone post with no thread at all: one
  // wasted round trip per post anyone opens, on a box that costs ~RM75/month.
  // Passing `undefined` is the hook's own "nothing to ask about" state.
  //
  // 1.24.00 D1.2 fix round: keyed on the RESOLVED `post.id`, never the raw route
  // param. The canonical rewrite above CHANGES that param, and usePostThread
  // resets its state during render whenever its ident changes — so keying on the
  // param made the rewrite blank the whole "This thread" block and refire an
  // identical, uncached `/thread/` request. `post` is necessarily resolved here
  // (this is only enabled when `post.thread` is set) and `/thread/` resolves a
  // legacy id exactly as it resolves a share code.
  const thread = usePostThread(post?.thread ? post.id : undefined);
  const isOwn = useIsOwn(post?.authorId);
  const openComposeSheet = useComposeSheetStore((s) => s.openSheet);

  // Right rail (memoised — re-publishes when author resolves)
  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="post-detail">
        <RailPanel id="author" label="About the author">
          <AboutAuthorPanel author={author} />
        </RailPanel>
        <RailPanel id="trending" label="Trending">
          <TrendingPanel />
        </RailPanel>
        <RailPanel id="people" label="Who to follow">
          <WhoToFollowPanel />
        </RailPanel>
      </RailGroup>
    ),
    [author],
  );
  useRightRail(post ? rightRail : null);

  // Fix-round item 27: same separator as ProfileView's title ("/ TruXSocial"),
  // was "on TruXSocial" here — standardized across post-detail and profile.
  usePageTitle(post && author ? `${author.name} (${author.handle}) / TruXSocial` : null);

  if (!post) {
    // Fix-round item 19: usePostHydration runs for guests too now (Growth
    // slice), so a guest landing on a RESTRICTED post (private author,
    // followers-only, etc. — not literally missing) gets the exact same
    // empty `post` this component always saw for "not found". The old
    // signed-out redirect used to send them to /signin as a side effect;
    // removing it lost that affordance entirely, leaving a guest looking at
    // "removed or never existed" for a post that's actually just gated
    // behind a sign-in. A signed-in viewer's "not found" stays as-is — for
    // them, empty really does mean gone.
    return (
      <Wrap>
        <PageHeader />
        {!isAuthenticated ? (
          <NotFound role="alert">
            <NotFoundText>
              This post isn&apos;t available to you right now. It may be private, restricted to
              followers, or require an account. Sign in to see if you have access.
            </NotFoundText>
            <HomeLink to={`/signin?next=${encodeURIComponent(location.pathname)}`}>
              Sign in
            </HomeLink>
          </NotFound>
        ) : (
          <NotFound role="alert">
            <NotFoundText>Post not found. It may have been removed or never existed.</NotFoundText>
            <HomeLink to="/">Back to home</HomeLink>
          </NotFound>
        )}
      </Wrap>
    );
  }

  function focusComposer() {
    composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    composerRef.current?.querySelector('textarea')?.focus();
  }

  // The ··· menu carries the same action; this is the one you can reach without
  // opening a menu, right where you just finished reading your own post.
  const canAddToThread = isOwn && post.published !== false && post.deleted !== true;

  return (
    // 1.24.00 C2 fix round: everything below is "the page OF this post", so a
    // thread chip pointing back HERE (the hero on a root's own page, and every
    // part listed under it) renders nothing instead of a link to this page.
    <ThreadPageScope postId={post.id}>
      <DiscussionToolsProvider key={`${post.id}:${currentUser?.id ?? 'guest'}`} post={post}>
        <Wrap>
          <PageHeader />
          {!isAuthenticated && (
            <GuestBanner message="You're viewing a post on TruXSocial. Sign in to reply, like, and share." />
          )}
          <HeroPostCard post={post} onComment={focusComposer} />
          {!post.deleted && !post.removed && <DiscussionGuideEntry postId={post.id} />}
          <DiscussionToolbar postId={post.id} />
          {canAddToThread && (
            <ThreadActionRow>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  startThreadPart(post, {
                    openComposeSheet,
                  })
                }
              >
                Add to thread
              </Button>
            </ThreadActionRow>
          )}
          {/* ThreadList owns the "is there anything to show?" rule (it filters out
            the post already in the hero, and renders nothing when that leaves
            nothing) — one place decides, not two. There used to be a
            `thread.root &&` gate here as well, which meant a thread whose root
            was deleted or Council-removed showed NONE of the parts that
            survived it — the server sends them precisely so they stay
            readable. */}
          <ThreadList
            root={thread.root}
            rootUnavailable={thread.rootUnavailable}
            parts={thread.parts}
            currentPostId={post.id}
          />
          <CommentsSection
            key={`${post.id}:${currentUser?.id ?? 'guest'}`}
            postId={post.id}
            ref={composerRef}
          />
        </Wrap>
      </DiscussionToolsProvider>
    </ThreadPageScope>
  );
}
